import React, { useEffect, useMemo, useImperativeHandle, forwardRef, useState, useRef, useCallback } from 'react';
import { Player, PlayerRef as RemotionPlayerRef } from '@remotion/player';
import { AbsoluteFill } from 'remotion';
import { Spin } from 'antd';
import { MultiSceneComposition, ScenePlaybackItem } from './MultiSceneComposition';
import { CompositorManifest, CompositorRecordingData } from '../types/manifest';
import { useSourceResolver } from '../hooks/useSourceResolver';
import { useCompositorManifest } from '../hooks/useCompositorManifest';
import { DEFAULT_FPS } from '../constants';
import { framesToMs, msToFrames } from '../utils/frameCalculator';
import { Scene, SceneTransition, TransitionType } from '../../../../types/scene';
import { buildRecordingDataFromScene, getSceneTransitionOut } from '../utils/sceneToRecordingData';

export interface MultiSceneCompositorPlayerRef {
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seekTo: (frame: number) => void;
  seekToSeconds: (seconds: number) => void;
  getCurrentFrame: () => number;
  getCurrentTimeMs: () => number;
  getDurationFrames: () => number;
  getDurationMs: () => number;
  isPlaying: () => boolean;
}

interface SceneInput {
  scene: Scene;
  recordingData: CompositorRecordingData;
}

interface MultiSceneCompositorPlayerProps {
  sceneInputs: SceneInput[];
  autoPlay?: boolean;
  loop?: boolean;
  showControls?: boolean;
  fps?: number;
  style?: React.CSSProperties;
  className?: string;
  onTimeUpdate?: (timeMs: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (error: Error) => void;
  onReady?: () => void;
}

async function preloadMedia(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    
    const isBlobUrl = url.startsWith('blob:');
    if (!isBlobUrl) {
      video.crossOrigin = 'anonymous';
    }
    
    let resolved = false;
    
    const cleanup = () => {
      if (resolved) return;
      resolved = true;
      video.removeEventListener('canplaythrough', handleReady);
      video.removeEventListener('loadeddata', handleReady);
      video.removeEventListener('error', handleError);
      video.pause();
      video.src = '';
      video.load();
    };
    
    const handleReady = () => { 
      cleanup(); 
      resolve(); 
    };
    
    const handleError = () => { 
      cleanup(); 
      reject(new Error(`Failed to preload video: ${url}`)); 
    };
    
    video.addEventListener('canplaythrough', handleReady);
    video.addEventListener('loadeddata', handleReady);
    video.addEventListener('error', handleError);
    video.src = url;
    video.load();
    
    const timeout = isBlobUrl ? 2000 : 15000;
    setTimeout(() => { 
      if (!resolved) {
        console.warn(`[preloadMedia] Timeout reached (${timeout}ms) for:`, url.slice(-40));
        cleanup(); 
        resolve(); 
      }
    }, timeout);
  });
}

interface MultiSceneCompositionWrapperProps {
  scenes: ScenePlaybackItem[];
  canvasWidth: number;
  canvasHeight: number;
}

const MultiSceneCompositionWrapper: React.FC<MultiSceneCompositionWrapperProps> = (props) => {
  if (!props.scenes || props.scenes.length === 0) {
    return <AbsoluteFill style={{ backgroundColor: '#1a1a1a' }} />;
  }
  return <MultiSceneComposition {...props} />;
};


export const MultiSceneCompositorPlayer = forwardRef<MultiSceneCompositorPlayerRef, MultiSceneCompositorPlayerProps>(
  (
    {
      sceneInputs,
      autoPlay = false,
      loop = false,
      showControls = true,
      fps = DEFAULT_FPS,
      style,
      className,
      onTimeUpdate,
      onPlay,
      onPause,
      onEnded,
      onError,
      onReady,
    },
    ref
  ) => {
    const playerRef = useRef<RemotionPlayerRef | null>(null);
    const [mediaPreloaded, setMediaPreloaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const { resolver } = useSourceResolver();

    const scenePlaybackItems = useMemo<ScenePlaybackItem[]>(() => {
      if (!sceneInputs || sceneInputs.length === 0) {
        return [];
      }
      
      return sceneInputs.map((input) => {
        const { scene, recordingData } = input;
        
        const transitionOutData = getSceneTransitionOut(scene);
        const transitionDurationFrames = Math.round(transitionOutData.duration * fps);
        
        let manifest: CompositorManifest | null = null;
        
        if (recordingData) {
          try {
            const { buildCompositorManifest } = require('../utils/manifestBuilder');
            
            // Debug: log audio stream data before building manifest
            console.log('[MultiSceneCompositorPlayer] Building manifest for scene:', scene.id, {
              hasAudioStream: !!recordingData.streams?.audio,
              audioSegmentCount: recordingData.streams?.audio?.segments?.length ?? 0,
              audioSegments: recordingData.streams?.audio?.segments?.map((s: any) => ({
                segmentIndex: s.segmentIndex,
                tsStart: s.tsStart,
                tsEnd: s.tsEnd,
                key: s.key?.substring(0, 50),
              })),
            });
            
            manifest = buildCompositorManifest(recordingData, {
              fps,
              sourceResolver: resolver,
              pendingBlobs: new Map(),
              pendingBlobUrls: new Map(),
            });
            
            // Debug: log audio layer after building manifest
            console.log('[MultiSceneCompositorPlayer] Built manifest audioLayer:', {
              sceneId: scene.id,
              hasAudioLayer: !!manifest?.audioLayer,
              audioSegmentCount: manifest?.audioLayer?.segments?.length ?? 0,
              audioSegments: manifest?.audioLayer?.segments?.map(s => ({
                segmentId: s.segmentId,
                startFrame: s.startFrame,
                durationFrames: s.durationFrames,
                volume: s.volume,
                sourceStatus: s.source?.status,
                sourceValue: s.source?.value?.substring(0, 50),
              })),
            });
          } catch (err) {
            console.error('[MultiSceneCompositorPlayer] Failed to build manifest for scene:', scene.id, err);
          }
        }
        
        const durationMs = recordingData?.duration || scene.duration || 0;
        const durationFrames = Math.ceil((durationMs / 1000) * fps);
        
        return {
          sceneId: scene.id,
          sceneTitle: scene.title,
          manifest: manifest!,
          durationFrames,
          transitionOut: {
            type: transitionOutData.type as TransitionType,
            durationFrames: transitionDurationFrames,
          },
        };
      }).filter(item => item.manifest !== null);
    }, [sceneInputs, fps, resolver]);

    const { totalDurationFrames, canvasConfig } = useMemo(() => {
      let total = 0;
      let width = 1920;
      let height = 1080;
      
      scenePlaybackItems.forEach((scene, index) => {
        total += scene.durationFrames;
        
        if (index < scenePlaybackItems.length - 1) {
          total -= scene.transitionOut.durationFrames;
        }
        
        if (scene.manifest?.canvas) {
          width = scene.manifest.canvas.width;
          height = scene.manifest.canvas.height;
        }
      });
      
      return {
        totalDurationFrames: Math.max(1, total),
        canvasConfig: { width, height },
      };
    }, [scenePlaybackItems]);

    // Log config only when scene data changes (not on every render)
    useEffect(() => {
      console.log('[MultiSceneCompositorPlayer] Config:', {
        sceneCount: scenePlaybackItems.length,
        totalDurationFrames,
        totalDurationSeconds: totalDurationFrames / fps,
        canvasConfig,
        scenes: scenePlaybackItems.map(s => ({
          id: s.sceneId,
          title: s.sceneTitle,
          durationFrames: s.durationFrames,
          transitionType: s.transitionOut.type,
          transitionDurationFrames: s.transitionOut.durationFrames,
        })),
      });
    }, [scenePlaybackItems, totalDurationFrames, canvasConfig, fps]);

    // Check if all sources are resolved (not in loading state)
    const allSourcesReady = useMemo(() => {
      for (const scene of scenePlaybackItems) {
        if (!scene.manifest) continue;
        
        for (const layer of scene.manifest.layers || []) {
          for (const segment of layer.segments || []) {
            if (segment.source?.status === 'loading') {
              return false;
            }
          }
        }
        
        if (scene.manifest.audioLayer?.segments) {
          for (const segment of scene.manifest.audioLayer.segments) {
            if (segment.source?.status === 'loading') {
              return false;
            }
          }
        }
      }
      return true;
    }, [scenePlaybackItems]);

    useEffect(() => {
      if (scenePlaybackItems.length === 0) {
        setMediaPreloaded(true);
        setIsLoading(false);
        return;
      }

      // Wait for all sources to be resolved before preloading
      if (!allSourcesReady) {
        return;
      }

      const preloadAll = async () => {
        setIsLoading(true);
        const urls: string[] = [];
        
        scenePlaybackItems.forEach(scene => {
          if (!scene.manifest) return;
          
          scene.manifest.layers?.forEach(layer => {
            layer.segments?.forEach(segment => {
              const url = segment.source?.value;
              if (url && segment.source?.status === 'ready' && !url.startsWith('blob:')) {
                urls.push(url);
              }
            });
          });
          
          if (scene.manifest.audioLayer?.segments) {
            scene.manifest.audioLayer.segments.forEach(segment => {
              const url = segment.source?.value;
              if (url && segment.source?.status === 'ready' && !url.startsWith('blob:')) {
                urls.push(url);
              }
            });
          }
        });

        console.log('[MultiSceneCompositorPlayer] Preloading', urls.length, 'media URLs');
        
        try {
          await Promise.all(urls.map(url => preloadMedia(url).catch(err => {
            console.warn('[MultiSceneCompositorPlayer] Failed to preload:', url, err);
          })));
        } catch (err) {
          console.warn('[MultiSceneCompositorPlayer] Preload error:', err);
        }
        
        setMediaPreloaded(true);
        setIsLoading(false);
      };

      preloadAll();
    }, [scenePlaybackItems, allSourcesReady]);
    
    // Track when Player is mounted using a callback ref
    const readyCalled = useRef(false);
    
    const handlePlayerMount = useCallback((node: RemotionPlayerRef | null) => {
      playerRef.current = node;
      if (node && mediaPreloaded && !isLoading && !readyCalled.current) {
        console.log('[MultiSceneCompositorPlayer] Player mounted via callback ref, calling onReady');
        readyCalled.current = true;
        // Small delay to ensure Player is fully initialized
        setTimeout(() => {
          onReady?.();
        }, 50);
      }
    }, [mediaPreloaded, isLoading, onReady]);

    useImperativeHandle(ref, () => ({
      play: () => {
        const currentFrame = playerRef.current?.getCurrentFrame() || 0;
        console.log(`[MultiSceneCompositorPlayer] PLAY_CALLED currentFrame=${currentFrame} hasRef=${!!playerRef.current}`);
        playerRef.current?.play();
      },
      pause: () => {
        const currentFrame = playerRef.current?.getCurrentFrame() || 0;
        console.log(`[MultiSceneCompositorPlayer] PAUSE_CALLED currentFrame=${currentFrame} hasRef=${!!playerRef.current}`);
        playerRef.current?.pause();
      },
      toggle: () => {
        const currentFrame = playerRef.current?.getCurrentFrame() || 0;
        const isPlaying = playerRef.current?.isPlaying() || false;
        console.log(`[MultiSceneCompositorPlayer] TOGGLE_CALLED currentFrame=${currentFrame} isPlaying=${isPlaying}`);
        playerRef.current?.toggle();
      },
      seekTo: (frame: number) => {
        console.log(`[MultiSceneCompositorPlayer] SEEK_TO frame=${frame}`);
        playerRef.current?.seekTo(frame);
      },
      seekToSeconds: (seconds: number) => {
        const frame = Math.round(seconds * fps);
        console.log(`[MultiSceneCompositorPlayer] SEEK_TO_SECONDS seconds=${seconds} frame=${frame}`);
        playerRef.current?.seekTo(frame);
      },
      getCurrentFrame: () => playerRef.current?.getCurrentFrame() || 0,
      getCurrentTimeMs: () => framesToMs(playerRef.current?.getCurrentFrame() || 0, fps),
      getDurationFrames: () => totalDurationFrames,
      getDurationMs: () => framesToMs(totalDurationFrames, fps),
      isPlaying: () => playerRef.current?.isPlaying() || false,
    }));

    // Track frame updates with detailed logging
    const lastLoggedFrameRef = useRef(-1);
    const frameJumpCountRef = useRef(0);
    const lastFrameRef = useRef(0);

    useEffect(() => {
      const player = playerRef.current;
      if (!player) return;

      const handlePlay = () => {
        const frame = player.getCurrentFrame();
        console.log(`[MultiSceneCompositorPlayer] 🎬 PLAY_EVENT frame=${frame}`);
        onPlay?.();
      };
      const handlePause = () => {
        const frame = player.getCurrentFrame();
        console.log(`[MultiSceneCompositorPlayer] ⏸️ PAUSE_EVENT frame=${frame}`);
        onPause?.();
      };
      const handleEnded = () => {
        console.log(`[MultiSceneCompositorPlayer] 🏁 ENDED_EVENT totalFrames=${totalDurationFrames}`);
        onEnded?.();
      };
      const handleError = (e: any) => {
        const message = e?.detail?.error?.message || e?.message || 'Unknown error';
        console.error(`[MultiSceneCompositorPlayer] ❌ ERROR_EVENT message=${message}`);
        onError?.(new Error(message));
      };
      const handleFrameUpdate = (e: { detail: { frame: number } }) => {
        const frame = e.detail.frame;
        const timeSec = (frame / fps).toFixed(2);
        
        // Detect frame jumps (going backwards or jumping forward significantly)
        const frameDiff = frame - lastFrameRef.current;
        if (frameDiff < -1 || frameDiff > 5) {
          frameJumpCountRef.current++;
          console.warn(`[MultiSceneCompositorPlayer] ⚠️ FRAME_JUMP from=${lastFrameRef.current} to=${frame} diff=${frameDiff} jumpCount=${frameJumpCountRef.current}`);
        }
        lastFrameRef.current = frame;
        
        // Log every second (30 frames)
        if (frame % 30 === 0 && frame !== lastLoggedFrameRef.current) {
          lastLoggedFrameRef.current = frame;
          console.log(`[MultiSceneCompositorPlayer] 📍 FRAME frame=${frame}/${totalDurationFrames} time=${timeSec}s`);
        }
        
        onTimeUpdate?.(framesToMs(frame, fps));
      };

      player.addEventListener('play', handlePlay);
      player.addEventListener('pause', handlePause);
      player.addEventListener('ended', handleEnded);
      player.addEventListener('error', handleError as any);
      player.addEventListener('frameupdate', handleFrameUpdate as any);

      return () => {
        player.removeEventListener('play', handlePlay);
        player.removeEventListener('pause', handlePause);
        player.removeEventListener('ended', handleEnded);
        player.removeEventListener('error', handleError as any);
        player.removeEventListener('frameupdate', handleFrameUpdate as any);
      };
    }, [fps, onPlay, onPause, onEnded, onError, onTimeUpdate, totalDurationFrames]);

    if (isLoading || !mediaPreloaded || !allSourcesReady) {
      return (
        <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1a1a1a' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (scenePlaybackItems.length === 0) {
      return (
        <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1a1a1a', color: 'white' }}>
          <div style={{ textAlign: 'center' }}>
            <p>No scenes available for playback</p>
          </div>
        </div>
      );
    }

    return (
      <Player
        ref={handlePlayerMount}
        component={MultiSceneCompositionWrapper}
        inputProps={{
          scenes: scenePlaybackItems,
          canvasWidth: canvasConfig.width,
          canvasHeight: canvasConfig.height,
        }}
        durationInFrames={totalDurationFrames}
        compositionWidth={canvasConfig.width}
        compositionHeight={canvasConfig.height}
        fps={fps}
        autoPlay={autoPlay}
        loop={loop}
        controls={showControls}
        style={style}
        className={className}
        numberOfSharedAudioTags={20}
        initiallyMuted={false}
        acknowledgeRemotionLicense
      />
    );
  }
);

MultiSceneCompositorPlayer.displayName = 'MultiSceneCompositorPlayer';

export default MultiSceneCompositorPlayer;
